/**
 * WizardState store (008 data-model, 002 editor) — template creation flow:
 * generate → mask → submit. The flow opens on an AI image-to-image "generate"
 * step (reference + product + instructions → a generated image); the generated
 * image becomes the case image the masking step operates on. The single "mask"
 * step merges the former detect + edit steps onto ONE page (run AI detection or
 * draw manually, then fine-tune in the MaskEditor, then submit). The legacy
 * "upload" step remains an alternate manual entry. Holds the picked generate
 * inputs, the generated image, the references the detect path masks, the
 * brokered detection result, and the working editor masks before submit.
 */
import { create } from "zustand";
import type { Detection, Mask, Polygon } from "@genz/engine-bind";
import type { CaseRuleKey, GenerationPrintArea, PickedFile } from "@genz/platform";

/** One rule text per prompt that carries one — the server's `CaseRuleKey`. */
export type CaseRules = Record<CaseRuleKey, string>;
import { DEFAULT_SOLID_BACKGROUND_HEX } from "../templates/backgroundColor.js";
import { ALL_VIEWS } from "../templates/views.js";

/**
 * WS-HF: `case` and `stages` are the two-stage flow's steps, and `case` is the wizard's new
 * ENTRY. `generate` and `upload` survive as the single-stage and manual entries, reachable from
 * `case`'s "Advanced: single-stage" disclosure.
 */
export type WizardStep = "case" | "stages" | "generate" | "upload" | "mask" | "submit";

export type WizardOrigin = "editor" | "generate";

/** The views a wizard run can author: the templates' own list, "back" first and primary. */
export const SELECTABLE_VIEWS = ALL_VIEWS;

/** A generation job's view as a wizard view; an unknown one falls back to "back". */
export function wizardView(view: string): string {
  return (SELECTABLE_VIEWS as readonly string[]).includes(view) ? view : "back";
}

/**
 * The product-specific camera-opening rules, ONE PER STAGE, verbatim from the owner's canvas.
 *
 * Each stage says something different about the same window: stage 1 must draw it OPEN, stage 2
 * must leave it UNPRINTED, and the repair must REMOVE what a render invented over it. One shared
 * paragraph would have pasted "draw it open" into the prompt that has to say "do not paint it".
 * They are fields on the first step rather than lines in a prompt because they are the text that
 * changes when the case does: *"If the case reference is changed, replace this product-specific
 * rule with its actual opening geometry."* Prefilled, because the case in front of the operator
 * today is the one these describe.
 */
export const DEFAULT_CASE_RULES: Readonly<CaseRules> = {
  // THE SERVER'S SENTENCES, VERBATIM (`CASE_RULE_DEFAULTS` in
  // `cloud/app/ai_relay/providers/prompt.py`): one product-specific sentence per prompt, the
  // rest of each paragraph being fixed prompt text the placeholder sits inside. A field left
  // at this value is sent EMPTY, and the server fills its own copy — so a default corrected
  // on the server reaches every job without a client release, and "Reset to default" and
  // "left alone" mean the same prompt. `case-rules-defaults.test.ts` pins all three against
  // the captured response of a job created with no rules and against the canvas document.
  clear:
    "THE CAMERA OPENING IS EXACTLY AS THE CASE CLOSE-UP SHOWS IT: the same count, shape, size and position of openings.",
  magenta:
    "The camera opening follows the supplied case geometry: keep every real opening fully open, with visible phone hardware photorealistic and unprinted. In Whole Back mode the full case lip takes magenta, including its inner and outer sloping walls; slight shading is permitted only there. In Inside Only mode the camera lip remains clear.",
  repair:
    "The camera opening matches the real case reference exactly: the same count, shape, size and position of openings.",
};

/** The AI-generated image carried out of the generate step (data-URI + bytes). */
export interface GeneratedImage {
  /** `data:<mediaType>;base64,...` — rendered directly in an <img>. */
  dataUri: string;
  /** Decoded image bytes — used as the case image the detect/refine path masks. */
  bytes: Uint8Array;
  mediaType: string;
  /** WS-AUTOKEY — which hand-over produced it: the two-stage case job's approved stage 2, the
   *  single-stage generate, or a Generate-page job (A2). All are Higgsfield-made (auto magenta
   *  key, Reference light); only a case job has a stage 2 to retry when the key finds no magenta. */
  source?: "case-job" | "single-stage" | "generation";
  /** A2 — the Generate-page job this image is the result of. */
  generationJobId?: string;
  /** Keep the server's selected printable-surface policy through the template hand-over. */
  printAreaMode?: GenerationPrintArea | null;
  /** A two-pass job's pass-1 image: the same frame with a clear case, kept under the print area. */
  clear?: { bytes: Uint8Array; mediaType: string };
  /** A3 — the automatic checks' notes on this image. Advisory: they never block Publish. */
  checkNotes?: string[];
  /** The case job's product height, for the coverage band; `null` = no height restriction. */
  productHeightPct?: number | null;
  /** WS-AUTOKEY fix round 2 — the case job this image IS the stage-2 result of. Stored ON the
   *  image at the hand-over, so Publish finishes (and the stage-2 retry asks about) this job
   *  and no other: the store's `caseJobId` can move on (a resumed or restored run) while this
   *  image is still on screen. */
  caseJobId?: string;
}

/** One view of a Generate run in the wizard: its image, its clear image and the job it came from. */
export interface RunView {
  view: string;
  image: GeneratedImage;
  clear?: Uint8Array;
  generationJobId: string;
}

/** A view's print area and cutouts. */
export interface ViewGeometry {
  printArea: Polygon;
  cutouts: Mask[];
}

interface WizardState {
  step: WizardStep;
  references: PickedFile[];
  /**
   * Generate-step inputs (image-to-image). BOTH slots accept MANY images — every
   * uploaded reference + product is sent together in one generation call.
   */
  referenceImages: PickedFile[];
  productImages: PickedFile[];
  /** The AI-generated image, carried into the detect/mask flow as the case. In a run, the
   *  active view's image. */
  generated?: GeneratedImage;
  /** A Generate run's views, one template for all of them; empty for a single image. */
  generatedViews: RunView[];
  /** The run view on screen. Its geometry is `printArea`/`cutouts`; every other view's is in
   *  `viewGeometry`, so the single-image flow keeps working on the view on screen. */
  activeView: string;
  viewGeometry: Record<string, ViewGeometry>;
  detection?: Detection;
  /** Working copies, edited in MaskEditor before submit. */
  printArea: Polygon;
  cutouts: Mask[];
  /**
   * #1 — Ephemeral creation fields that USED to live as Wizard component state and
   * were silently lost on navigation. They live here so the in-progress template
   * survives a remount (nav away → back) and the discard guard can reset them.
   */
  prompt: string;
  brandArea: Polygon | null;
  phoneModelId: string;
  caseStyleId: string;
  /** Base material/type of the case (spec 022): tpu | glass | … (drives compositing). */
  baseType: string;
  /** Optional human-chosen template code; server generates one if left blank. */
  code: string;
  /** Owner decision 2026-09-24 — the customer's Details. `brand` narrows the Model list (the
   *  template stores the phone model, whose brand it carries); `templateName` defaults from
   *  brand + model until the customer edits it (`templateNameEdited`). */
  brand: string;
  templateName: string;
  templateNameEdited: boolean;
  category: string;
  outputFormat: string;
  /** Task 6: chosen Background option-group choice key (persisted into the version payload's
   *  render_settings.background on publish). "" = none chosen yet. */
  background: string;
  /** WS-BG: the "Solid color" swatch, `"#rrggbb"`. Resolved into
   *  `render_settings.background_color` on publish; ignored for every other choice. */
  backgroundHex: string;
  views: string[];
  /**
   * WS-HF — the three reference photographs the two-stage flow is built from: the phone back,
   * the empty clear case, and a camera-area crop of that same case.
   */
  casePhone?: PickedFile;
  caseEmpty?: PickedFile;
  caseCrop?: PickedFile;
  /** The product-specific opening geometry, one text per stage — each stage's `{case_rules}`. */
  caseRules: CaseRules;
  /**
   * The case job this run created, if any.
   *
   * IN THE STORE, not in component state, and that is the resume rule within a session: a
   * navigation away and back re-reads THIS job instead of starting a second one. A generation
   * the operator paid for that the screen has forgotten is a generation they pay for twice.
   *
   * This store is memory, so a RELOAD empties it. That case is covered elsewhere: `CaseFlow`
   * also remembers the job id under the hash of its three photographs in `localStorage`
   * (`generate/deviceRunRegistry.ts`, via `wizard/caseRunKey.ts`), so picking the same photos
   * again and pressing Start resumes the job rather than creating it twice.
   */
  caseJobId?: string;
  /** "Make template" from Only mock-ups: the case image has no magenta, and the view publishes
   *  marked `mockup_only` unless a print area is drawn. */
  mockupOnly: boolean;
  /** Whether the creation Wizard is currently OPEN (drives TemplatesPanel; survives a
   *  remount so returning to Templates re-shows the in-progress wizard). */
  open: boolean;
  /** WHICH page opened it: the admin Editor's Templates tab or the Generate page (A2). Each
   *  page shows only the wizard it opened. Absent ⇒ the Editor. */
  origin?: WizardOrigin;
  templateId?: string;
  versionId?: string;
  /** Version NUMBER — the cloud routes submit/approve by number, not id (002/003). */
  versionNumber?: number;
  /** Optimistic-concurrency token echoed back on submit. */
  versionToken?: string;
  setStep(step: WizardStep): void;
  setPrompt(prompt: string): void;
  setBrandArea(brandArea: Polygon | null): void;
  setPhoneModelId(id: string): void;
  setCaseStyleId(id: string): void;
  setBaseType(baseType: string): void;
  setCode(code: string): void;
  setBrand(brand: string): void;
  /** `edited: true` for the customer's own typing; a default from brand + model passes false. */
  setTemplateName(name: string, edited: boolean): void;
  setCategory(category: string): void;
  setOutputFormat(format: string): void;
  setBackground(background: string): void;
  setBackgroundHex(backgroundHex: string): void;
  setViews(views: string[]): void;
  /** WS-HF — one of the three case photographs. `undefined` removes it. */
  setCasePhoto(slot: "phone" | "empty" | "crop", file: PickedFile | undefined): void;
  /** Set ONE stage's rule text. */
  setCaseRule(stage: keyof CaseRules, text: string): void;
  setCaseJobId(caseJobId: string | undefined): void;
  setOpen(open: boolean): void;
  setOrigin(origin: WizardOrigin | undefined): void;
  /** The case image(s) the operator picked or dropped. Also forgets any Higgsfield image and
   *  its case job — see the implementation. */
  setReferences(files: PickedFile[]): void;
  /** Append to / clear / remove-one from the generate-step reference slot. */
  addReferenceImages(files: PickedFile[]): void;
  removeReferenceImage(index: number): void;
  /** Append to / clear / remove-one from the generate-step product slot. */
  addProductImages(files: PickedFile[]): void;
  removeProductImage(index: number): void;
  /**
   * Record the generated image AND wire it into the detect path: the generated
   * bytes become the single `references` entry (named so the detect step treats
   * it as the case image), so detect → edit → submit continue unchanged.
   */
  setGenerated(generated: GeneratedImage): void;
  setDetection(d: Detection): void;
  setPrintArea(p: Polygon): void;
  setCutouts(c: Mask[]): void;
  /** Show another view of the run: the view on screen keeps its geometry. */
  setActiveView(view: string): void;
  /** Write a run view's geometry, on screen or not. */
  setViewGeometry(view: string, geometry: Partial<ViewGeometry>): void;
  setVersion(templateId: string, versionId: string, versionNumber: number, versionToken: string): void;
  /**
   * ED-10 — persist the created template id ON ITS OWN, before a version exists.
   *
   * `submit()` used to record the id only via `setVersion`, i.e. AFTER `createVersion` +
   * `patchRenderSettings` had both succeeded. A failure in between left `templateId` unset, so
   * every retry called `createTemplate` again and ORPHANED another empty template — and with a
   * Code filled in, the second create 409'd ("code already in use") forever, making the run
   * unrecoverable. Storing it immediately lets the retry reuse the template it already made.
   */
  setTemplateId(templateId: string): void;
  reset(): void;
}

/** The generated image becomes the case image the detect/refine path masks. */
const caseImage = (generated: GeneratedImage) => ({
  generated,
  references: [{ name: "generated-case.png", bytes: generated.bytes, mime: generated.mediaType }],
});

/** The Generate jobs the wizard's template is made from: every run view's, else the image's. */
export function generationJobIds(s: Pick<WizardState, "generatedViews" | "generated">): string[] {
  if (s.generatedViews.length > 0) return s.generatedViews.map((v) => v.generationJobId);
  return s.generated?.generationJobId ? [s.generated.generationJobId] : [];
}

/** A run view's geometry: the working copy for the view on screen, else the kept one. */
export function geometryOf(
  s: Pick<WizardState, "activeView" | "printArea" | "cutouts" | "viewGeometry">, view: string,
): ViewGeometry {
  if (view === s.activeView) return { printArea: s.printArea, cutouts: s.cutouts };
  return s.viewGeometry[view] ?? { printArea: [], cutouts: [] };
}

const initial = {
  // WS-HF — the two-stage flow is the wizard's entry. The single-stage generate is still there,
  // behind the first step's "Advanced: single-stage" disclosure.
  step: "case" as WizardStep,
  references: [] as PickedFile[],
  referenceImages: [] as PickedFile[],
  productImages: [] as PickedFile[],
  generated: undefined,
  generatedViews: [] as RunView[],
  activeView: "",
  viewGeometry: {} as Record<string, ViewGeometry>,
  detection: undefined,
  printArea: [] as Polygon,
  cutouts: [] as Mask[],
  prompt: "",
  brandArea: null as Polygon | null,
  phoneModelId: "",
  caseStyleId: "",
  baseType: "",
  code: "",
  brand: "",
  templateName: "",
  templateNameEdited: false,
  // Mirrors the Wizard's first CATEGORIES option (tpu_phone_case) so the select has a
  // valid default selection on open.
  category: "tpu_phone_case",
  outputFormat: "",
  background: "",
  backgroundHex: DEFAULT_SOLID_BACKGROUND_HEX,
  views: ["back"] as string[],
  casePhone: undefined as PickedFile | undefined,
  caseEmpty: undefined as PickedFile | undefined,
  caseCrop: undefined as PickedFile | undefined,
  caseRules: { ...DEFAULT_CASE_RULES } as CaseRules,
  caseJobId: undefined as string | undefined,
  mockupOnly: false,
  open: false,
  origin: undefined as WizardOrigin | undefined,
  templateId: undefined,
  versionId: undefined,
  versionNumber: undefined,
  versionToken: undefined,
};

export const useWizardStore = create<WizardState>((set) => ({
  ...initial,
  setStep: (step) => set({ step }),
  setPrompt: (prompt) => set({ prompt }),
  setBrandArea: (brandArea) => set({ brandArea }),
  setPhoneModelId: (phoneModelId) => set({ phoneModelId }),
  setCaseStyleId: (caseStyleId) => set({ caseStyleId }),
  setBaseType: (baseType) => set({ baseType }),
  setCode: (code) => set({ code }),
  setBrand: (brand) => set({ brand }),
  setTemplateName: (templateName, edited) => set({ templateName, templateNameEdited: edited }),
  setCategory: (category) => set({ category }),
  setOutputFormat: (outputFormat) => set({ outputFormat }),
  setBackground: (background) => set({ background }),
  setBackgroundHex: (backgroundHex) => set({ backgroundHex }),
  setViews: (views) => set({ views }),
  setCasePhoto: (slot, file) =>
    set(slot === "phone" ? { casePhone: file } : slot === "empty" ? { caseEmpty: file } : { caseCrop: file }),
  setCaseRule: (stage, text) => set((s) => ({ caseRules: { ...s.caseRules, [stage]: text } })),
  setCaseJobId: (caseJobId) => set({ caseJobId }),
  setOpen: (open) => set({ open }),
  setOrigin: (origin) => set({ origin }),
  // WS-AUTOKEY fix round: a photo the operator picked or dropped REPLACES the Higgsfield image.
  // "From Higgsfield" belongs to the image, so it goes with it: no automatic magenta key, no
  // Flat seed, no "Back to stage 2", and no case job finished on Publish for a real photo.
  setReferences: (references) => set({ references, generated: undefined, caseJobId: undefined }),
  addReferenceImages: (files) =>
    set((s) => ({ referenceImages: [...s.referenceImages, ...files] })),
  removeReferenceImage: (index) =>
    set((s) => ({ referenceImages: s.referenceImages.filter((_, i) => i !== index) })),
  addProductImages: (files) =>
    set((s) => ({ productImages: [...s.productImages, ...files] })),
  removeProductImage: (index) =>
    set((s) => ({ productImages: s.productImages.filter((_, i) => i !== index) })),
  setGenerated: (generated) => set(caseImage(generated)),
  setDetection: (detection) =>
    set({ detection, printArea: detection.printArea, cutouts: detection.cutouts }),
  setPrintArea: (printArea) => set({ printArea }),
  setCutouts: (cutouts) => set({ cutouts }),
  setActiveView: (view) => set((s) => {
    const next = s.generatedViews.find((v) => v.view === view);
    if (!next || view === s.activeView) return {};
    const kept = s.viewGeometry[view] ?? { printArea: [], cutouts: [] };
    return {
      viewGeometry: { ...s.viewGeometry, [s.activeView]: { printArea: s.printArea, cutouts: s.cutouts } },
      activeView: view,
      ...kept,
      detection: undefined,
      ...caseImage({ ...next.image, source: "generation" }),
    };
  }),
  setViewGeometry: (view, geometry) => set((s) => (view === s.activeView ? geometry : {
    viewGeometry: { ...s.viewGeometry, [view]: { ...geometryOf(s, view), ...geometry } },
  })),
  setVersion: (templateId, versionId, versionNumber, versionToken) =>
    set({ templateId, versionId, versionNumber, versionToken }),
  setTemplateId: (templateId) => set({ templateId }),
  reset: () => set({ ...initial }),
}));

/** A2 — a fresh run on the Mask step, on an image the Generate page made, with its Details. */
export function openWizardOnGeneration(
  image: GeneratedImage,
  details: Pick<WizardState, "category" | "brand" | "phoneModelId" | "code" | "templateName" | "views">
    & Partial<Pick<WizardState, "caseStyleId" | "baseType">>,
): void {
  useWizardStore.setState({ ...initial, ...details, origin: "generate", open: true, step: "mask" });
  useWizardStore.getState().setGenerated({ ...image, source: "generation" });
}

/** A Generate run's ready views (back first) on the Mask step: one template with all of them. */
export function openWizardOnRun(
  views: RunView[],
  details: Pick<WizardState, "category" | "brand" | "phoneModelId" | "code" | "templateName">
    & Partial<Pick<WizardState, "caseStyleId" | "baseType">>,
): void {
  const [first] = views;
  useWizardStore.setState({
    ...initial, ...details, views: views.map((v) => v.view), generatedViews: views, activeView: first.view,
    origin: "generate", open: true, step: "mask",
  });
  useWizardStore.getState().setGenerated({ ...first.image, source: "generation" });
}

/** The Details "Make template" carries from a mock-up run into the wizard. */
export type MockupWizardDetails = Pick<
  WizardState, "category" | "brand" | "phoneModelId" | "caseStyleId" | "baseType" | "code" | "templateName" | "views"
>;

/** A super-admin's template from a mock-up: the clear image is the case image, like a photo — no
 *  automatic magenta key — and the print area starts empty, so the view publishes marked. */
export function openWizardOnMockup(image: { bytes: Uint8Array; mediaType: string }, details: MockupWizardDetails): void {
  useWizardStore.setState({
    ...initial, ...details, mockupOnly: true, origin: "editor", open: true, step: "mask",
    references: [{ name: "mockup-case.png", bytes: image.bytes, mime: image.mediaType }],
  });
}

/**
 * #1 — Whether the wizard holds UNSAVED creation work. A finished run (step "submit")
 * is not dirty; an empty run is not dirty. Any picked image, typed prompt, generated
 * image, or drawn geometry counts.
 */
export function isWizardDirty(s: WizardState): boolean {
  if (s.step === "submit") return false;
  return (
    s.referenceImages.length > 0 ||
    s.productImages.length > 0 ||
    s.references.length > 0 ||
    !!s.generated ||
    s.prompt.trim().length > 0 ||
    s.printArea.length > 0 ||
    s.cutouts.length > 0 ||
    // ED-11: the whole DETAILS fieldset was ignored, so an operator who filled in the model,
    // style, code, base type, background or brand box — but hadn't picked an image yet — was
    // told there was nothing to lose, and Cancel discarded it silently. Compared against the
    // `initial` defaults so an untouched form is still "not dirty".
    s.phoneModelId !== initial.phoneModelId ||
    s.caseStyleId !== initial.caseStyleId ||
    s.baseType !== initial.baseType ||
    s.code.trim().length > 0 ||
    s.category !== initial.category ||
    s.outputFormat !== initial.outputFormat ||
    s.brandArea !== null ||
    JSON.stringify(s.views) !== JSON.stringify(initial.views) ||
    /* WS-HF — a picked case photograph, an edited rule, and above all a CASE JOB are unsaved
       work. The job especially: it is credits already spent, and discarding the run without
       saying so would throw them away silently. */
    !!s.casePhone || !!s.caseEmpty || !!s.caseCrop ||
    (Object.keys(DEFAULT_CASE_RULES) as (keyof CaseRules)[]).some(
      (k) => s.caseRules[k] !== DEFAULT_CASE_RULES[k],
    ) ||
    !!s.caseJobId ||
    // `background` is deliberately NOT counted: the Wizard AUTO-SEEDS it from the option group's
    // default on mount, so including it would report every freshly-opened wizard as dirty.
    // A template that reached the cloud (created but not yet submitted) is definitely unsaved work.
    !!s.templateId
  );
}

/**
 * #1 — Navigation guard. When the wizard is OPEN with unsaved work, ask the user to
 * confirm discarding it. Returns true to PROCEED with navigation (and resets the
 * wizard); false to STAY (data preserved). A no-op (returns true) when there's nothing
 * to lose. `confirmFn` is injectable for tests; defaults to `window.confirm`.
 */
export function confirmDiscardWizard(
  confirmFn: (message: string) => boolean = (m) =>
    typeof window !== "undefined" ? window.confirm(m) : true,
): boolean {
  const s = useWizardStore.getState();
  if (!s.open || !isWizardDirty(s)) return true;
  const ok = confirmFn("Your template isn't saved yet. Discard your edits?");
  if (ok) s.reset();
  return ok;
}
